if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      this.productOptionsEl = this.productSection.querySelector('.product-options');
      this.optionsTriggers = [...this.querySelectorAll('[sticky-atc-options-trigger]')];
      // Back-compat: keep a single reference for existing usages.
      this.optionsTrigger = this.optionsTriggers[0] ?? null;
      this._lastOptionsTrigger = null;
      this.variantTextEl = this.querySelector('[data-sticky-variant-text]');
      const backdropId = this.dataset.stickyAtcBackdropId;
      const modalId = this.dataset.stickyAtcModalId;
      this.optionsModalBackdrop = backdropId ? document.getElementById(backdropId) : null;
      this.optionsModal = modalId ? document.getElementById(modalId) : this.querySelector('.sticky-atc-options-modal');
      this.optionsModalBody = this.optionsModal?.querySelector('[data-sticky-atc-options-body]') ?? null;
      this._variantPickerPlaceholder = null;
      this._movedVariantPicker = null;

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      if (this._onKeyDown) document.removeEventListener('keydown', this._onKeyDown);
      if (this._onVariantChange && this.productSection) {
        this.productSection.removeEventListener('on:variant:change', this._onVariantChange);
      }
      this._stickyAtcPanelResizeObs?.disconnect();
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md && !this.productOptionsEl) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productOptionsEl && !theme.mediaMatches.md) {
            // 移动端：从顶部下滑，当规格区进入视口后再显示粘性底栏；滑过规格区后保持显示
            const { top } = entry.boundingClientRect;
            const fullyBelowViewport = top >= window.innerHeight;
            this.classList.toggle('sticky-atc-panel--out', fullyBelowViewport);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);
      if (this.productOptionsEl) observer.observe(this.productOptionsEl);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);

      this.bindOptionsModal();
      this.bindVariantSync();
    }

    bindVariantSync() {
      if (!this.productSection || !this.variantTextEl) return;

      this._onVariantChange = (evt) => {
        const variant = evt?.detail?.variant;
        if (!variant) return;

        let nextText = '';
        if (Array.isArray(variant.options) && variant.options.length) {
          nextText = variant.options.join(' | ');
        } else if (variant.title && variant.title !== 'Default Title') {
          nextText = variant.title;
        }

        if (nextText) this.variantTextEl.textContent = nextText;
      };

      this.productSection.addEventListener('on:variant:change', this._onVariantChange);
    }

    syncOptionsModalAnchor() {
      if (!this.optionsModal) return;
      const h = Math.ceil(this.getBoundingClientRect().height);
      this.optionsModal.style.setProperty('--sticky-atc-panel-height', `${h}px`);
    }

    bindOptionsModal() {
      if (!this.optionsTriggers?.length || !this.optionsModal || !this.optionsModalBody) return;

      if (!this._stickyAtcPanelResizeObs && typeof ResizeObserver !== 'undefined') {
        this._stickyAtcPanelResizeObs = new ResizeObserver(() => this.syncOptionsModalAnchor());
        this._stickyAtcPanelResizeObs.observe(this);
      }
      this.syncOptionsModalAnchor();

      const closeEls = [
        ...(this.optionsModalBackdrop?.querySelectorAll('[data-sticky-atc-options-close]') ?? []),
        ...this.optionsModal.querySelectorAll('[data-sticky-atc-options-close]'),
      ];
      closeEls.forEach((el) => {
        el.addEventListener('click', () => this.closeOptionsModal());
      });

      // Close when clicking on the backdrop/container itself (outside the dialog).
      // This must not affect any interactions inside the dialog.
      if (!this.optionsModal.dataset.backdropClickBound) {
        this.optionsModal.dataset.backdropClickBound = '1';
        this.optionsModal.addEventListener('click', (e) => {
          if (e.target === this.optionsModal) this.closeOptionsModal();
        });
      }
      if (this.optionsModalBackdrop && !this.optionsModalBackdrop.dataset.backdropClickBound) {
        this.optionsModalBackdrop.dataset.backdropClickBound = '1';
        this.optionsModalBackdrop.addEventListener('click', (e) => {
          if (e.target === this.optionsModalBackdrop) this.closeOptionsModal();
        });
      }

      this.optionsTriggers.forEach((triggerEl) => {
        triggerEl.addEventListener('click', () => {
          // Toggle behavior: clicking trigger closes if already open.
          if (this.isOptionsModalOpen()) {
            this.closeOptionsModal();
            return;
          }

          this._lastOptionsTrigger = triggerEl;
          this.openOptionsModal();
          try {
            sensors.track('StickyOptionClick');
          } catch {
            // ignore
          }
        });
      });

      this._onKeyDown = (e) => {
        if (e.key === 'Escape' && this.isOptionsModalOpen()) this.closeOptionsModal();
      };
      document.addEventListener('keydown', this._onKeyDown);
    }

    isOptionsModalOpen() {
      return this.optionsModal && this.optionsModal.hidden === false;
    }

    getVariantPickerElement() {
      if (!this.productSection) return null;
      // Keep same DOM to preserve state/listeners and avoid duplicate IDs.
      return this.productSection.querySelector('.product-options variant-picker, .product-options .no-js-hidden variant-picker, variant-picker');
    }

    openOptionsModal() {
      const variantPicker = this.getVariantPickerElement();
      if (!variantPicker) return;

      if (!this._variantPickerPlaceholder) {
        this._variantPickerPlaceholder = document.createElement('div');
        this._variantPickerPlaceholder.setAttribute('data-sticky-atc-variant-picker-placeholder', '');
      }

      // Insert placeholder only once, and only if picker is currently in DOM.
      if (!this._variantPickerPlaceholder.parentNode) {
        variantPicker.parentNode.insertBefore(this._variantPickerPlaceholder, variantPicker);
      }

      // Move the actual picker into modal.
      this.optionsModalBody.innerHTML = '';
      this.optionsModalBody.appendChild(variantPicker);
      this._movedVariantPicker = variantPicker;

      // Make visible first, then animate (avoid hidden killing transitions).
      this.syncOptionsModalAnchor();
      if (this.optionsModalBackdrop) this.optionsModalBackdrop.hidden = false;
      this.optionsModal.hidden = false;
      document.documentElement.classList.add('sticky-atc-options-modal-open');
      requestAnimationFrame(() => {
        if (this.optionsModalBackdrop) this.optionsModalBackdrop.classList.add('is-open');
        this.optionsModal.classList.add('is-open');
      });

      const closeBtn = this.optionsModal.querySelector('.sticky-atc-options-modal__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    closeOptionsModal() {
      if (!this.optionsModal) return;

      // Start closing animation.
      if (this.optionsModalBackdrop) this.optionsModalBackdrop.classList.remove('is-open');
      this.optionsModal.classList.remove('is-open');

      // Move picker back to its original spot.
      if (this._movedVariantPicker && this._variantPickerPlaceholder && this._variantPickerPlaceholder.parentNode) {
        this._variantPickerPlaceholder.parentNode.insertBefore(this._movedVariantPicker, this._variantPickerPlaceholder);
      }

      if (this._variantPickerPlaceholder && this._variantPickerPlaceholder.parentNode) {
        this._variantPickerPlaceholder.parentNode.removeChild(this._variantPickerPlaceholder);
      }

      this._movedVariantPicker = null;

      let afterCloseDone = false;
      const afterClose = () => {
        if (afterCloseDone) return;
        afterCloseDone = true;
        this.optionsModal.hidden = true;
        if (this.optionsModalBackdrop) this.optionsModalBackdrop.hidden = true;
        document.documentElement.classList.remove('sticky-atc-options-modal-open');
        this.optionsModal.removeEventListener('transitionend', afterClose);
        if (this.optionsModalBackdrop) this.optionsModalBackdrop.removeEventListener('transitionend', afterClose);
      };
      // Wait for transform/opacity transition to finish; fallback timeout for safety.
      this.optionsModal.addEventListener('transitionend', afterClose);
      if (this.optionsModalBackdrop) this.optionsModalBackdrop.addEventListener('transitionend', afterClose);
      window.setTimeout(afterClose, 300);

      // Return focus to trigger.
      const focusEl = this._lastOptionsTrigger || this.optionsTrigger;
      if (focusEl) focusEl.focus({ preventScroll: true });
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
